import { useEffect, useState } from 'react';
import { useLocation, useParams } from 'wouter';
import { invites as invitesApi } from '../lib/api.js';
import { Button } from '../components/ui/Button.js';
import { useT } from '../i18n/index.js';
import { useAuth } from '../context/AuthContext.js';
import { takeAuthCallbackToken } from '../lib/auth-callback.js';

interface InviteDetails {
  homeId: string;
  homeName: string;
  inviterEmail: string;
  role: string;
  expiresAt: string;
}

export default function InviteAcceptPage() {
  const params = useParams<{ token: string }>();
  const [fragmentToken, setFragmentToken] = useState<string | null>(null);
  const token = params.token === 'accept' ? fragmentToken : params.token;
  useEffect(() => {
    const value = takeAuthCallbackToken();
    if (value) setFragmentToken(value);
  }, []);
  const [, navigate] = useLocation();
  const t = useT();
  const { accessToken, setCurrentHomeId } = useAuth();
  const [invite, setInvite] = useState<InviteDetails | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) return;
    invitesApi
      .get(token)
      .then(setInvite)
      .catch(() => setError(t('common.error')));
  }, [token, t]);

  const onAccept = async () => {
    if (!token) return;
    if (!accessToken) {
      navigate('/login');
      return;
    }
    setBusy(true);
    try {
      const { homeId } = await invitesApi.accept(token);
      setCurrentHomeId(homeId);
      navigate('/');
    } catch {
      setError(t('common.error'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div
        className="w-full max-w-md p-6 rounded-xl border flex flex-col gap-4"
        style={{ backgroundColor: 'var(--ac-surface)', borderColor: 'var(--ac-border)' }}
      >
        <h1 className="text-xl font-semibold" style={{ color: 'var(--ac-text)' }}>
          Invite to join
        </h1>
        {error ? (
          <div style={{ color: 'var(--ac-error)' }}>{error}</div>
        ) : invite ? (
          <>
            <div style={{ color: 'var(--ac-text)' }}>
              <strong>{invite.inviterEmail}</strong> invited you to{' '}
              <strong>{invite.homeName}</strong> as <em>{invite.role}</em>.
            </div>
            <Button onClick={onAccept} disabled={busy}>
              Accept invite
            </Button>
          </>
        ) : (
          <div style={{ color: 'var(--ac-text-muted)' }}>{t('common.loading')}</div>
        )}
      </div>
    </div>
  );
}
